import {useAuthContext} from "../../auth/useAuthContext";
import {useSnackbar} from "notistack";
import {useLocales} from "../../locales";
import {useNavigate} from "react-router-dom";
import {useTable} from "../../components/table";
import {dispatch, useSelector} from "../../redux/store";
import {useCallback, useEffect, useState} from "react";
import {deleteTripInfoById, searchTripInfo} from "../../api/tripInfo";
import {getAllTripTimesByOperator} from "../../redux/slices/departures";
import {PATH_DASHBOARD} from "../../routes/path";

const INITIAL_FILTERS = {
    comment: '',
    lineName: '',
    departureTime: '',
    operatorName: '',
    operatorId: null,
};

export function useCommentsHook(isPreview) {
    const {user} = useAuthContext();
    const {enqueueSnackbar} = useSnackbar();
    const {translate} = useLocales();
    const navigate = useNavigate();
    const table = useTable({defaultOrderBy: 'id'});
    const {page, rowsPerPage, orderBy, order} = table;

    const {tripTimes} = useSelector(state => state.departures);
    const {operatorsBE} = useSelector(state => state.operatorsBE);

    const [filters, setFilters] = useState(INITIAL_FILTERS);
    const [comments, setComments] = useState({content: [], totalElements: 0});
    const [loading, setLoading] = useState(false);
    const [searchTrigger, setSearchTrigger] = useState(0);

    const fetchComments = async () => {
        setLoading(true);
        try {
            const foundTime = tripTimes?.find(t => t.id === filters.departureTime)?.combinedTime;
            const response = await searchTripInfo({
                ...filters,
                departureTime: foundTime || null,
                operatorId: user?.id,
                onlyMain: !isPreview,
                page: table.page,
                rowsPerPage: table.rowsPerPage,
                orderBy: table.orderBy,
                order: table.order,
            });
            setComments(response.data);
        } catch (error) {
            console.error("fetchComments error:", error);
            enqueueSnackbar(translate('errorOnServer'), {variant: 'error'});
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchComments();
    }, [searchTrigger, page, rowsPerPage, orderBy, order]);

    useEffect(() => {
        if (user?.id) {
            dispatch(getAllTripTimesByOperator({
                lineName: filters.lineName,
                operatorId: filters.operatorId || user.id,
                stationId: null,
                onlyMain: !isPreview,
                onlyWithComments: true,
            }));
        }
    }, [filters.lineName, user?.id]);

    const handleFilterChange = useCallback((event) => {
        const {name, value} = event.target;
        setFilters(prevFilters => ({...prevFilters, [name]: value}));
    }, []);

    const handleDepartureTimeChange = useCallback((newValue) => {
        setFilters(prevFilters => ({...prevFilters, departureTime: newValue}));
    }, []);

    const handleOperatorChange = useCallback((newValue) => {
        setFilters(prevFilters => ({
            ...prevFilters, operatorName: newValue?.name,
            operatorId: newValue?.id
        }));
    }, []);

    const handleSearch = () => {
        table.setPage(0);
        setSearchTrigger(prev => prev + 1);
    };

    const handleResetFilters = useCallback(() => {
        setFilters(INITIAL_FILTERS);
        table.setPage(0);
        setSearchTrigger(prev => prev + 1);
    }, [table]);


    const handleDeleteRow = useCallback(async (data) => {
        try {
            await deleteTripInfoById(data.id);
            enqueueSnackbar("Uspešno brisanje!", {variant: "success"});
            fetchComments(); // Ponovo dohvati podatke nakon brisanja
        } catch (e) {
            console.error(e, "deleteTripInfoById");
            enqueueSnackbar("Greška prilikom brisanja", {variant: "error"});
        }
    }, [fetchComments, enqueueSnackbar]);

    const handleEditRow = useCallback((row) => {
        navigate(PATH_DASHBOARD.comments.edit(row.id), {state: row});
    }, [navigate]);

    const isFiltered = Object.values(filters).some(value => value !== '');

    return {
        table,
        comments,
        loading,
        filters,
        isFiltered,
        tripTimes,
        operatorsBE,
        handleFilterChange,
        handleDepartureTimeChange,
        handleOperatorChange,
        handleResetFilters,
        handleDeleteRow,
        handleEditRow,
        handleSearch,
    };
}
